// Módulo PURO (sin imports, sin DB) del «oído local» (cadena huayruro-ojo-oido-agentes, tramo 09; D-O2..D-O4).
// La compu de la botica escucha el mostrador (el celular le pasa el audio por Tailscale), transcribe ahí mismo,
// saca las señales con reglas fijas y sube SOLO esto: un lote de señales y un resumen con números.
//
// INVARIANTE 1 de la cadena: ni el audio ni la transcripción salen de la botica. Por eso el lote tiene una
// LISTA CERRADA de campos en cada nivel: un campo de más («texto», «transcripcion», «frase», «audio»…) se
// rechaza con 400 en vez de guardarse callado, y el nombre del producto es corto (máx. 6 palabras): no cabe
// una conversación. VETO D-N5: nada aquí identifica a quien habló; no hay campo de persona posible.

export type TipoSenalOido = "faltante" | "venta_posible";

// Las reglas que puede disparar la compu (lista cerrada; la compu manda el id, nunca la frase oída).
export const REGLAS_OIDO = {
  no_hay: { tipo: "faltante", etiqueta: "no hay" },
  no_tenemos: { tipo: "faltante", etiqueta: "no tenemos" },
  se_acabo: { tipo: "faltante", etiqueta: "se acabó" },
  no_queda: { tipo: "faltante", etiqueta: "ya no queda" },
  agotado: { tipo: "faltante", etiqueta: "está agotado" },
  falta: { tipo: "faltante", etiqueta: "falta" },
  me_da: { tipo: "venta_posible", etiqueta: "me da" },
  deme: { tipo: "venta_posible", etiqueta: "deme" },
  vendame: { tipo: "venta_posible", etiqueta: "véndame" },
  me_llevo: { tipo: "venta_posible", etiqueta: "me llevo" },
  cobrame: { tipo: "venta_posible", etiqueta: "cóbrame" },
} as const satisfies Record<string, { tipo: TipoSenalOido; etiqueta: string }>;

export type ReglaOido = keyof typeof REGLAS_OIDO;
export const esReglaOido = (x: unknown): x is ReglaOido => typeof x === "string" && Object.prototype.hasOwnProperty.call(REGLAS_OIDO, x);

const CAMPOS_LOTE = new Set(["senales", "resumen"]);
const CAMPOS_SENAL = new Set(["senal_id", "tipo", "oido_at", "items", "confianza", "regla"]);
const CAMPOS_ITEM = new Set(["nombre", "cantidad", "producto_id"]);
const CAMPOS_RESUMEN = new Set(["fecha", "trozos", "sin_habla", "errores", "segundos_audio", "senales"]);

export const MAX_SENALES_LOTE = 50;
export const MAX_ITEMS_SENAL = 10;
export const MAX_NOMBRE_OIDO = 60;
export const MAX_PALABRAS_NOMBRE = 6;
const ID_RE = /^[A-Za-z0-9._-]{4,80}$/;
const NOMBRE_RE = /^[\p{L}\p{N} .,%/+-]+$/u;
const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_ATRAS_MS = 7 * 86_400_000; // una compu sin internet sube lo de la última semana
const MAX_ADELANTE_MS = 10 * 60_000; // tolera relojes un poco adelantados, no fechas inventadas
const MAX_CONTEO = 1_000_000;

export type ItemOido = { nombre: string; cantidad: number | null; productoId: string | null };
export type SenalOido = { senalId: string; tipo: TipoSenalOido; regla: ReglaOido; oidoAt: string; confianza: number; items: ItemOido[] };
export type ResumenOido = { fecha: string; trozos: number; sinHabla: number; errores: number; segundosAudio: number; senales: number };
export type LoteOido = { senales: SenalOido[]; resumen: ResumenOido | null };
export type ResultadoLoteOido = { ok: true; lote: LoteOido } | { ok: false; error: string };

const esObjeto = (x: unknown): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x);

function campoExtra(o: Record<string, unknown>, permitidos: Set<string>, donde: string): string | null {
  for (const k of Object.keys(o)) if (!permitidos.has(k)) return `campo no permitido en ${donde}: ${k}`;
  return null;
}

const conteo = (x: unknown): number | null => (typeof x === "number" && Number.isInteger(x) && x >= 0 && x <= MAX_CONTEO ? x : null);

function validarItem(raw: unknown): ItemOido | string {
  if (!esObjeto(raw)) return "cada ítem debe ser un objeto";
  const extra = campoExtra(raw, CAMPOS_ITEM, "el ítem");
  if (extra) return extra;
  if (typeof raw.nombre !== "string") return "nombre inválido";
  const nombre = raw.nombre.replace(/\s+/g, " ").trim();
  if (!nombre || nombre.length > MAX_NOMBRE_OIDO || !NOMBRE_RE.test(nombre)) return "nombre inválido";
  if (nombre.split(" ").length > MAX_PALABRAS_NOMBRE) return `nombre con más de ${MAX_PALABRAS_NOMBRE} palabras`;
  let cantidad: number | null = null;
  if (raw.cantidad !== undefined && raw.cantidad !== null) {
    if (typeof raw.cantidad !== "number" || !Number.isInteger(raw.cantidad) || raw.cantidad < 1 || raw.cantidad > 999) return "cantidad inválida";
    cantidad = raw.cantidad;
  }
  let productoId: string | null = null;
  if (raw.producto_id !== undefined && raw.producto_id !== null) {
    if (typeof raw.producto_id !== "string" || !ID_RE.test(raw.producto_id)) return "producto_id inválido";
    productoId = raw.producto_id;
  }
  return { nombre, cantidad, productoId };
}

function validarSenal(raw: unknown, ahora: number): SenalOido | string {
  if (!esObjeto(raw)) return "cada señal debe ser un objeto";
  const extra = campoExtra(raw, CAMPOS_SENAL, "la señal");
  if (extra) return extra;
  if (typeof raw.senal_id !== "string" || !ID_RE.test(raw.senal_id)) return "senal_id inválido";
  if (!esReglaOido(raw.regla)) return "regla inválida";
  const regla = raw.regla;
  if (raw.tipo !== REGLAS_OIDO[regla].tipo) return "el tipo no corresponde a la regla";
  if (typeof raw.oido_at !== "string" || raw.oido_at.length > 40) return "oido_at inválido";
  const t = Date.parse(raw.oido_at);
  if (Number.isNaN(t)) return "oido_at inválido";
  if (t > ahora + MAX_ADELANTE_MS) return "oido_at en el futuro";
  if (t < ahora - MAX_ATRAS_MS) return "oido_at de hace más de 7 días";
  if (typeof raw.confianza !== "number" || !(raw.confianza >= 0 && raw.confianza <= 1)) return "confianza inválida";
  if (!Array.isArray(raw.items) || raw.items.length === 0 || raw.items.length > MAX_ITEMS_SENAL) return `items: de 1 a ${MAX_ITEMS_SENAL}`;
  if (raw.tipo === "faltante" && raw.items.length !== 1) return "un faltante lleva un solo producto";
  const items: ItemOido[] = [];
  for (const it of raw.items) {
    const v = validarItem(it);
    if (typeof v === "string") return v;
    items.push(v);
  }
  return { senalId: raw.senal_id, tipo: REGLAS_OIDO[regla].tipo, regla, oidoAt: new Date(t).toISOString(), confianza: Math.round(raw.confianza * 1000) / 1000, items };
}

function validarResumen(raw: unknown): ResumenOido | string {
  if (!esObjeto(raw)) return "el resumen debe ser un objeto";
  const extra = campoExtra(raw, CAMPOS_RESUMEN, "el resumen");
  if (extra) return extra;
  if (typeof raw.fecha !== "string" || !FECHA_RE.test(raw.fecha)) return "fecha del resumen inválida";
  const trozos = conteo(raw.trozos);
  const sinHabla = conteo(raw.sin_habla);
  const errores = conteo(raw.errores);
  const segundosAudio = conteo(raw.segundos_audio);
  const senales = conteo(raw.senales);
  if (trozos === null || sinHabla === null || errores === null || segundosAudio === null || senales === null) return "conteos del resumen inválidos";
  return { fecha: raw.fecha, trozos, sinHabla, errores, segundosAudio, senales };
}

// Valida el lote completo. Todo o nada: una señal mala rechaza el lote (la compu lo ve en su registro y no
// lo reintenta en bucle), así nunca entra a la D1 media información.
export function validarLoteOido(raw: unknown, ahoraIso: string): ResultadoLoteOido {
  if (!esObjeto(raw)) return { ok: false, error: "el lote debe ser un objeto" };
  const extra = campoExtra(raw, CAMPOS_LOTE, "el lote");
  if (extra) return { ok: false, error: extra };
  const ahora = Date.parse(ahoraIso);
  const senalesRaw = raw.senales ?? [];
  if (!Array.isArray(senalesRaw) || senalesRaw.length > MAX_SENALES_LOTE) return { ok: false, error: `senales: hasta ${MAX_SENALES_LOTE} por lote` };
  const senales: SenalOido[] = [];
  const vistos = new Set<string>();
  for (const s of senalesRaw) {
    const v = validarSenal(s, ahora);
    if (typeof v === "string") return { ok: false, error: v };
    if (vistos.has(v.senalId)) return { ok: false, error: `senal_id repetido: ${v.senalId}` };
    vistos.add(v.senalId);
    senales.push(v);
  }
  let resumen: ResumenOido | null = null;
  if (raw.resumen !== undefined && raw.resumen !== null) {
    const r = validarResumen(raw.resumen);
    if (typeof r === "string") return { ok: false, error: r };
    resumen = r;
  }
  if (senales.length === 0 && !resumen) return { ok: false, error: "lote vacío" };
  return { ok: true, lote: { senales, resumen } };
}

// Texto corto para la bandeja (no es lo que se dijo: es la regla que disparó, armada desde la lista cerrada).
export const etiquetaReglaOido = (regla: string): string | null => (esReglaOido(regla) ? REGLAS_OIDO[regla].etiqueta : null);
